// THE tax-rate normaliser and per-jurisdiction tax derivation. Every report goes through this
// file — there is to be no inline rate arithmetic anywhere else in the report layer.
//
// ═══ UNITS DIFFER BY SOURCE, NOT BY STATE ═══
// Verified by hand on LaSalle parcel 4343-019-027 (LA County, 2021):
//     taxable 57,726,764 × 1.178467% = $680,290.86
// which matches the AMOUNT column on the parcel tax screen exactly. So a rate stored by the app
// (parcel_taxes.tax_rate, tax_bills.total_tax_rate) is a PERCENT — 1.178467 means 1.178467%.
//
// SigerTax supplies MILLAGE (18.1106 = 1.81106%), and the importer is meant to divide by 10.
// Whether it always did is UNKNOWN, which is exactly why the plausibility band below is not
// optional: a millage value that slipped through undivided is 10x too large and would print a
// $6.8M tax bill on a $680K parcel.
//
// ⛔ DO NOT BRANCH ON STATE. Only California parcels are loaded today; Texas arrives with the next
// import and will exercise the millage path. A `if (state === 'CA')` here would be correct for
// exactly as long as it takes that import to land. `state` is accepted and logged for diagnosis,
// never used to pick units.
//
// ═══ THE BANDS — TWO OF THEM, AND THE REASON MATTERS ═══
// The brief said "assert the resulting effective rate lands in roughly 0.5%–5%". Applied to a
// SINGLE stored rate that is right. Applied per-jurisdiction it is WRONG, and it was caught by the
// Texas test case below: a Texas parcel carries county 0.35%, city 0.55%, school 1.10%, hospital
// 0.17% simultaneously. Only their SUM belongs in the 0.5%–5% band; three of those four components
// sit under 0.5% individually. A per-line 0.5% floor would have silently refused most Texas
// jurisdiction rates the moment that import landed — a blank Taxes column across a whole state.
//
// So:
//   • PER LINE  — a WIDE sanity bound (0.01%–10%). Its only job is catching a unit error: millage
//     stored without the /10 reads as 18.11%, which no single jurisdiction charges.
//   • AGGREGATE — the tight 0.5%–5% band, enforced in aggregateTax() on the EFFECTIVE rate
//     (total tax / total taxable). This is the check the brief actually wants, applied where it is
//     meaningful. On a single-jurisdiction parcel the two collapse to the same test, so today's
//     California data is gated exactly as specified.
//
// Either failure returns NULL and logs loudly. A blank Taxes column is recoverable in front of a
// client; a wrong one is not. Same judgement as computeTaxBill's multi-jurisdiction guard: refuse
// to compute rather than display a wrong number.

import { TAX_BILL_STATUS } from '../constants/statuses';

export const MIN_EFFECTIVE_RATE = 0.005;   // 0.5%  — aggregate
export const MAX_EFFECTIVE_RATE = 0.05;    // 5%    — aggregate
// ⛔ PER STATE (Philip 2026-10-22): Cook County, IL bills run ~7% — a real rate, not a 10x error — so Illinois's
// whole-parcel band reaches 10%. Every other state keeps 0.5%–5%. One table: the reports, the card's tax figures
// (computeTaxBill → aggregateTax), scripts/check-tax-bills.mjs and scripts/sql/tax_rates_out_of_band.sql all use these numbers.
// ⛔ NEW YORK (Philip 2026-10-27): NYC charges its rate on the billable assessed value (45% of market for class 4), so a
// class 4 bill runs 10.6%–10.85% (class 2 ~12.4%) — one line, so the single-line cap rises with it. Both reach 15%;
// millage stored as percent still fails (NYC's rate in mills reads 108%). DTAG 45-01 20th Ave (Blk 775, Lot 1).
// ⛔ OHIO (Philip 2026-10-28): Ohio taxes 35% of market, so Cuyahoga's full rate is 12.8–13.7% and its effective rate (after
// the 920 reduction) ~10.0–10.3%. Same 15% as New York, line and whole parcel. DTAG Cleveland Hopkins 029-16-010.
const STATE_MAX_EFFECTIVE_RATE = { IL: 0.10, NY: 0.15, OH: 0.15 };
const STATE_MAX_LINE_RATE = { NY: 0.15, OH: 0.15 };
export const MIN_LINE_RATE = 0.0001;       // 0.01% — one jurisdiction's component
export const MAX_LINE_RATE = 0.10;         // 10%   — catches millage-as-percent (18.1106 -> 18.11%)
export const effectiveRateBand = (state) => {
  const s = String(state || '').trim().toUpperCase();
  return {
    lo: MIN_EFFECTIVE_RATE,
    hi: STATE_MAX_EFFECTIVE_RATE[s] ?? MAX_EFFECTIVE_RATE,
    lineHi: STATE_MAX_LINE_RATE[s] ?? MAX_LINE_RATE,
  };
};

// Sources whose values arrive as millage (per $1,000) rather than percent (per $100).
const MILLAGE_SOURCES = new Set(['sigertax', 'millage']);

const num = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v ?? '').replace(/[%$\s,]/g, '');
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};

// ═══ parseRateCell — THE ONE CLASSIFIER FOR A tax_bills.jurisdiction_rates LINE ═══
//
// ⛔ NEVER TEST `tax_rate IS NULL` TO FIND A FIXED CHARGE. On an LA County bill a direct
// assessment prints a CONTACT PHONE NUMBER in the rate column, and the scanner writes it straight
// through despite an extraction prompt that explicitly says to return null for those lines.
// Verified on parcel 4343-019-027 / 2021 / instalment 1 — six of eleven lines hold values like
// "(833) 275-7297" and "(626) 458-5165" where a rate belongs.
//
// Every value is stored as a STRING, and real rates are not normalised either: ".042929" has no
// leading zero. So the classifier has to be shape-based, not null-based.
//
// This is deliberately robust to whatever the scanner writes NEXT rather than tuned to today's
// failure mode: a bare decimal in a plausible range is a rate; anything else is a flat charge.
//
//   rated line   = parseRateCell(line.tax_rate) !== null
//   fixed charge = everything else
//
// The scanner ignoring its own prompt is a WRITE-PATH bug, logged and deliberately not chased here.
const RATE_SHAPE = /^\d*\.?\d+$/;    // 1.000000 | .042929 | 0.0035 — no sign, no comma, no separator
export const MAX_PLAUSIBLE_RATE_CELL = 20;   // percent or mills; above this it is not a rate

export function parseRateCell(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 && raw <= MAX_PLAUSIBLE_RATE_CELL ? raw : null;
  let s = String(raw).trim();
  if (!s) return null;
  if (s.endsWith('%')) s = s.slice(0, -1).trim();
  if (/[()]/.test(s)) return null;            // "(833) 275-7297"
  if (/[a-z]/i.test(s)) return null;          // any letter — never part of a rate
  if (!RATE_SHAPE.test(s)) return null;       // hyphens, commas, spaces, slashes -> "833-275-7297"
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return null;
  return n > 0 && n <= MAX_PLAUSIBLE_RATE_CELL ? n : null;   // "8332757297" parses but is absurd
}

// ═══ RATE RECOVERY — reconstructing a rate the extractor lost, from the bill's own arithmetic ═══
//
// ⛔ THE SIGNAL, which neither of us predicted and which was already sitting in the data. On
// 8940-436-179 / 2021 the general tax levy is the ONLY line carrying a taxable_value. Direct
// assessments never do — they are flat charges with nothing to levy against. So:
//
//     taxable_value present + tax_rate unreadable  =  a RATED line that lost its rate
//
// and the rate is recoverable exactly: 1520 / 152000 = 1.000000%, which is what the bill prints.
//
// ⛔ IT IS A TRIGGER, NOT A DETECTOR, and the difference matters. Four of the five genuinely rated
// lines on that same bill have taxable_value null, so a wrap that ate UNIFIED SCHOOLS' rate instead
// would be invisible here. Recovery reaches the lines that happen to carry a taxable value; the
// aggregate band in normaliseRate is what actually CATCHES a lost rate, whichever line lost it.
//
// ⛔ RECOVERY IS SELF-FULFILLING AND MUST NOT BE TRUSTED ON ITS OWN. amount / taxable_value
// reconciles against that same amount BY CONSTRUCTION, so "it reconciles" proves nothing — a flat
// charge that wrongly carried a taxable value would reconcile just as neatly. The validator is the
// aggregate band applied to the resulting sumRate by the caller, and it separates the cases:
//
//     before recovery            0.175188%   outside 0.5%–5%   refused
//     after recovering the levy  1.175188%   inside            accepted
//     recovering a flat charge   0.200208%   still outside     refused
//
// So this function may recover freely: a recovery that does not bring the parcel into a plausible
// whole-parcel rate is thrown away with everything else that hangs off it.
//
// ⛔ READ-TIME ONLY. Nothing here writes. The report layer does not repair the database from a read
// surface — that would destroy the evidence of what the scanner actually produced, which is the
// only record we have of how often this happens.
const positiveNum = (v) => { const n = num(v); return n != null && n > 0 ? n : null; };

export function recoverLineRate(line) {
  if (parseRateCell(line?.tax_rate) != null) return null;   // it has a readable rate; nothing lost
  const taxable = positiveNum(line?.taxable_value);
  const amount = positiveNum(line?.amount);
  if (taxable == null || amount == null) return null;
  const implied = (amount / taxable) * 100;                 // percent — the unit the cells are in
  return implied > 0 && implied <= MAX_PLAUSIBLE_RATE_CELL ? implied : null;
}

// ═══ A REDUCTION LINE CARRIES A NEGATIVE RATE (Philip 2026-10-28) ═══
// Ohio's "920 Reduction" (DTAG Cleveland Hopkins 029-16-010) is stored as its own line at −(full − effective rate), so the
// bill's lines add up to the net tax AND to the printed effective rate. parseRateCell refuses anything not positive —
// rightly, for scanned cells — so a negative rate counts only when the line says it is a reduction both ways: a
// minus-signed rate cell AND a negative amount. → the (negative) percent, or null.
function reductionRate(line) {
  const s = String(line?.tax_rate ?? '').trim();
  const amount = num(line?.amount);
  if (!s.startsWith('-') || amount == null || amount >= 0) return null;
  const r = parseRateCell(s.slice(1));
  return r == null ? null : -r;
}

// ⛔ THE ONE CLASSIFIER. isRatedLine, sumRatedRate and splitJurisdictionLines all go through it, so
// recovery cannot be live in one and absent in another — two functions disagreeing about which
// lines are rated is the same two-homes failure this file exists to prevent.
export function classifyLine(line) {
  const printed = parseRateCell(line?.tax_rate);
  if (printed != null) return { rated: true, rate: printed, recovered: null };
  const reduction = reductionRate(line);
  if (reduction != null) return { rated: true, rate: reduction, recovered: null };
  const implied = recoverLineRate(line);
  if (implied == null) return { rated: false, rate: null, recovered: null };
  return {
    rated: true,
    rate: implied,
    recovered: {
      name: line?.name ?? null,
      rawRate: line?.tax_rate ?? null,      // what the scanner wrote where a rate belongs
      impliedRate: implied,
      amount: num(line?.amount),
      taxableValue: num(line?.taxable_value),
    },
  };
}

export const isRatedLine = (line) => classifyLine(line).rated;

// Σ of the RATED lines' rates — the consolidated ad valorem rate, in whatever unit the cells are
// in (percent for LA). Recovered rates are included. null when no line is rated, never 0.
export function sumRatedRate(lines) {
  let sum = null;
  (Array.isArray(lines) ? lines : []).forEach((l) => {
    const { rate } = classifyLine(l);
    if (rate != null) sum = (sum ?? 0) + rate;
  });
  return sum;
}

// Σ of EVERY line's amount — rated and fixed together. This is the full tax bill.
// ⛔ Not tax_bills.total_ad_valorem / total_non_ad_valorem: those are mis-mapped on this data
// (instalment 1 of 4343-019-027/2021 carries 577,267.64, the general levy alone, where instalment 2
// carries 340,145.43, a correct half-year). Derive from the lines, never from the totals columns.
export function sumLineAmounts(lines) {
  let sum = null;
  (Array.isArray(lines) ? lines : []).forEach((l) => {
    const a = num(l?.amount);
    if (a != null) sum = (sum ?? 0) + a;
  });
  return sum;
}

// Split for reporting/diagnostics:
//   { rated, fixed, recovered, sumRate, sumRatedAmount, sumFixedAmount, total }
// `recovered` is the per-line evidence for every rate reconstructed by classifyLine — empty on a
// clean bill. The CALLER logs it and decides whether to surface a marker, because only the caller
// knows which parcel and year this is and whether the aggregate band accepted the result.
export function splitJurisdictionLines(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const rated = [];
  const fixed = [];
  const recovered = [];
  let sumRate = null;
  list.forEach((l) => {
    const c = classifyLine(l);
    if (!c.rated) { fixed.push(l); return; }
    rated.push(l);
    sumRate = (sumRate ?? 0) + c.rate;
    if (c.recovered) recovered.push(c.recovered);
  });
  return {
    rated, fixed, recovered, sumRate,
    sumRatedAmount: sumLineAmounts(rated),
    sumFixedAmount: sumLineAmounts(fixed),
    total: sumLineAmounts(list),
  };
}

/**
 * Normalise a stored rate to a decimal multiplier.
 *   normaliseRate(1.178467)                        -> 0.01178467
 *   normaliseRate(18.1106, { source:'sigertax' })  -> 0.0181106
 *
 * @param rawRate the stored value (number or string; '%' and ',' tolerated)
 * @param opts    { state, source, context, total } — state/context are for LOGGING ONLY.
 *                `total: true` says this value is already a whole-parcel effective rate, so the
 *                tight 0.5%–5% band applies instead of the wide per-line one.
 * @returns decimal multiplier, or null when it is missing or implausible
 */
export function normaliseRate(rawRate, opts = {}) {
  const { state = null, source = null, context = '', total = false } = opts;
  const raw = num(rawRate);
  if (raw == null || raw === 0) return null;

  // Percent (per 100) unless the SOURCE says millage (per 1000).
  const decimal = MILLAGE_SOURCES.has(String(source || '').toLowerCase())
    ? raw / 1000
    : raw / 100;

  const band = effectiveRateBand(state);
  const lo = total ? band.lo : MIN_LINE_RATE;
  const hi = total ? band.hi : band.lineHi;
  if (decimal < lo || decimal > hi) {
    console.warn(
      `[reports/taxRate] REFUSING a rate outside the plausible band: raw=${rawRate} ` +
      `source=${source || 'app(percent)'} -> ${(decimal * 100).toFixed(6)}% ` +
      `(band ${lo * 100}%–${hi * 100}%, ${total ? 'whole-parcel effective' : 'single jurisdiction'})` +
      `${state ? ` state=${state}` : ''}${context ? ` ${context}` : ''}. ` +
      'Returning null — a blank tax figure is recoverable, a 10x-wrong one is not. ' +
      'Most likely cause: millage stored without the /10 the importer should apply.'
    );
    return null;
  }
  return decimal;
}

// ═══ A BILL'S RATE (Philip 2026-10-27) ═══
// Rates come only from bills we've worked through — never imported. A bill's rate is its own line rates where it has
// them, else its printed total rate, else its EFFECTIVE rate: the bill's ad valorem ÷ its taxable value, labelled
// "effective" wherever it shows. The bill is the year's lowest regular installment that isn't rejected (pickYearBill):
// its total_ad_valorem is meant to be the year's, duplicated onto every installment (yearAdValorem). A supplemental is prorated, so it never sets
// the year's rate. One rule for the card's Taxes tab, the reports, the Tax Rates report and Bill Check.
export const isSupplementalBill = (b) => (b?.supplemental_seq != null && b.supplemental_seq !== '')
  || /supplemental/i.test(String(b?.bill_type || ''));

export function pickYearBill(bills) {
  return (Array.isArray(bills) ? bills : [])
    .filter((b) => b && !b.superseded_at && b.status !== TAX_BILL_STATUS.REJECTED && !isSupplementalBill(b))
    .sort((a, b) => (a.installment_number ?? Infinity) - (b.installment_number ?? Infinity))[0] || null;
}

// A bill's printed total rate (percent), band-checked; null when it has none or it is implausible.
export function printedBillRate(bill, { state = null, context = '' } = {}) {
  const d = normaliseRate(bill?.total_tax_rate, { state, total: true, context: `${context} printed rate` });
  return d == null ? null : d * 100;
}

// The year's ad valorem for the effective rate: the year's billed total less its non-ad valorem when every installment
// is in (billedYearTotal, the figure the year's Taxes print), else the bill's own total_ad_valorem. The billed total
// comes first because total_ad_valorem is sometimes one installment's, not the year's (HI 3-8-001-160-0000 2021).
export function yearAdValorem(billedTotal, bill) {
  const t = num(billedTotal);
  return t != null ? t - (num(bill?.total_non_ad_valorem) || 0) : num(bill?.total_ad_valorem);
}

// Ad valorem ÷ taxable as a percent, band-checked. `taxable` is the bill's own taxable value, else the caller's (the
// year's value). Null when either is missing or zero (a $0 exempt bill has no rate) or the result is implausible.
export function effectiveBillRate({ adValorem, taxable, state = null, context = '' }) {
  const a = num(adValorem), t = num(taxable);
  if (!(a > 0) || !(t > 0)) return null;
  const d = normaliseRate((a / t) * 100, { state, total: true, context: `${context} effective rate` });
  return d == null ? null : d * 100;
}

/**
 * ═══ MULTI-JURISDICTION AGGREGATE ═══
 * Tax for ONE parcel-year across however many jurisdictions it has.
 *
 * ⛔ Σ(rate_j × taxable_j), NOT one rate against a summed taxable value. src/utils/taxBill.js:49
 * computeTaxBill() does the latter — it sums taxable across jurisdictions and applies a single
 * rate — and explicitly returns null for multi-jurisdiction parcels because those taxable values
 * are not additive. Per-jurisdiction rate math does not exist anywhere else in the codebase; this
 * is it.
 *
 * With one jurisdiction the two formulas agree exactly, so this costs nothing on today's data
 * (every loaded parcel is single-jurisdiction LA County) and prevents a rewrite when Texas lands
 * carrying county + city + school + hospital + college rates on one parcel simultaneously.
 *
 * @param lines [{ jurisdiction, taxableValue, rate, source, state }]
 * @param opts  { context } for logging
 * @returns {
 *   tax:        number|null,   // null when NO line could be computed
 *   partial:    boolean,       // some lines computed, some refused — the figure is understated
 *   lines:      [{ jurisdiction, taxableValue, rate, tax, skipped, reason }],
 *   skipped:    number,
 *   taxableTotal: number|null  // informational only; never multiply this by a single rate
 * }
 */
export function aggregateTax(lines, opts = {}) {
  const { context = '' } = opts;
  const src = Array.isArray(lines) ? lines : [];
  const band = effectiveRateBand(opts.state ?? src.find((l) => l?.state)?.state);
  const out = [];
  let tax = 0;
  let computed = 0;
  let skipped = 0;
  let taxableTotal = null;

  src.forEach((line) => {
    const taxable = num(line?.taxableValue);
    const rate = normaliseRate(line?.rate, {
      state: line?.state,
      source: line?.source,
      context: `${context} jurisdiction=${line?.jurisdiction || '—'}`,
    });
    if (taxable != null) taxableTotal = (taxableTotal || 0) + taxable;

    // ⛔ A $0 BASE IS $0 TAX WHATEVER THE RATE (Philip, 2026-10-11). Tampa Airport 1124088000 is fully exempt —
    // taxable $0 on County and Public Schools — and read "computed unavailable — no rate for County, Public
    // Schools": a rate was demanded to multiply by zero. The line computes as 0, with no rate.
    if (taxable === 0) {
      computed += 1;
      out.push({ jurisdiction: line?.jurisdiction || null, taxableValue: 0, rate, tax: 0, skipped: false, reason: null });
      return;
    }
    if (taxable == null || rate == null) {
      skipped += 1;
      out.push({
        jurisdiction: line?.jurisdiction || null, taxableValue: taxable, rate: null, tax: null,
        skipped: true, reason: taxable == null ? 'no taxable value' : 'rate missing or implausible',
      });
      return;
    }
    const t = taxable * rate;
    tax += t;
    computed += 1;
    out.push({ jurisdiction: line?.jurisdiction || null, taxableValue: taxable, rate, tax: t, skipped: false, reason: null });
  });

  // ⛔ THE TIGHT BAND, applied HERE — on the whole-parcel effective rate, which is the only level
  // at which 0.5%–5% is a meaningful assertion. Measured against the taxable of the lines that
  // actually computed, so a partial result is judged on its own terms rather than against a
  // denominator that includes the lines it had to skip.
  const computedTaxable = out.filter((l) => !l.skipped).reduce((s, l) => s + l.taxableValue, 0);
  if (computed && computedTaxable > 0) {
    const effective = tax / computedTaxable;
    if (effective < band.lo || effective > band.hi) {
      console.warn(
        `[reports/taxRate] REFUSING a parcel: effective rate ${(effective * 100).toFixed(6)}% ` +
        `across ${computed} jurisdiction(s) is outside ${band.lo * 100}%–${band.hi * 100}%` +
        `${context ? ` ${context}` : ''}. tax=${tax.toFixed(2)} taxable=${computedTaxable}. ` +
        'Returning null rather than a figure that is likely 10x wrong.'
      );
      return { tax: null, partial: false, lines: out, skipped: src.length, taxableTotal, refused: 'effective rate out of band' };
    }
  }

  return {
    tax: computed ? tax : null,           // no line computed -> blank, never 0
    partial: computed > 0 && skipped > 0, // understated: the caller should not present it as final
    lines: out,
    skipped,
    taxableTotal,
    refused: null,
  };
}

/**
 * Savings for one parcel-year: (proposed − final) valued at the same per-jurisdiction rates.
 * Both sides must be computable or the answer is null — a saving derived from a proposed value
 * that computed and a final value that didn't is not a saving, it is the proposed tax.
 *
 * @param proposedLines [{ jurisdiction, taxableValue, rate, source, state }]
 * @param finalLines    same shape, same jurisdictions
 */
export function aggregateSavings(proposedLines, finalLines, opts = {}) {
  const p = aggregateTax(proposedLines, { ...opts, context: `${opts.context || ''} proposed` });
  const f = aggregateTax(finalLines, { ...opts, context: `${opts.context || ''} final` });
  if (p.tax == null || f.tax == null) return { savings: null, proposedTax: p.tax, finalTax: f.tax, partial: p.partial || f.partial };
  return { savings: p.tax - f.tax, proposedTax: p.tax, finalTax: f.tax, partial: p.partial || f.partial };
}

// ═══ PER-LINE RECONCILIATION — amount ≈ taxable × rate, with a DERIVED tolerance ═══
//
// ⛔ ONE CHECK, ONE TOLERANCE, ONE PLACE. This lived in TWO places with DIFFERENT tolerances:
// JurisdictionBreakdown used max($2, amount × 1%) to draw its ✓/⚠ column, DocumentScanner's
// advReconcile used a flat $2 to decide which anchor to trust. So a line could be ⚠ on screen and
// still count as "consistent" for the anchor choice, or the reverse. Both now call this.
//
// ⛔ THE TOLERANCE IS DERIVED FROM THE PRINTED RATE, NOT PICKED. A bill prints its rate rounded to a
// fixed number of decimals, so the true product is only knowable to ±half an ulp of that rate across
// the base: a rate printed as ".043759" is anywhere in [.0437585, .0437595], which on a $3,828,391
// base is ±$0.019. Add a cent for the amount's own rounding and the honest tolerance is ~$0.03 — not
// the $16.75 that max($2, 1%) allowed on that line.
//
// Verified against the case that prompted this: LA County Community College, taxable 3,828,391 at
// .043759% = 1,675.26 exactly. The misread pair (rate .043729, amount 1,673.38) is $1.88 out —
// 65x this tolerance, and comfortably INSIDE both of the tolerances it replaced.
//
// A line with no taxable_value (a flat direct assessment) has nothing to reconcile and returns
// ok:null — neutral, never a failure. Same for a zero line.
export function lineTolerance(rawRate, taxable, divisor) {
  const base = Math.abs(Number(taxable) || 0);
  const s = String(rawRate ?? '').trim();
  const dp = s.includes('.') ? (s.split('.')[1] || '').length : 0;
  // No decimal point at all → assume a whole-number rate is exact rather than inventing ±0.5 of slack.
  const halfUlp = dp ? Math.pow(10, -dp) / 2 : 0;
  return 0.01 + (halfUlp / divisor) * base;
}

// { ok, expected, delta, tol } — ok === null means "nothing to check on this line".
export function lineReconcile(line, divisor) {
  const rate = parseRateCell(line?.tax_rate);
  // ⛔ A MISSING AMOUNT USED TO RECONCILE AS ZERO AND REPORT A FAILURE. Number(String(null ?? '')) is 0, and
  // Number.isFinite(0) is true, so `haveAmount` was true for a line with no amount at all — the expectation
  // then came out as the whole taxable × rate and the line was marked WRONG. taxable escaped this only by
  // accident, through its `!== 0` test. The same guard cannot be reused for amount: a genuine zero-levy line
  // is a real figure and must still be checked, so absence is tested BEFORE coercion.
  // This function's own comment promises "nothing to reconcile and returns ok:null — neutral, never a
  // failure"; that promise was kept for a missing taxable_value and broken for a missing amount.
  const taxable = Number(String(line?.taxable_value ?? '').replace(/[$,]/g, ''));
  const haveTaxable = Number.isFinite(taxable) && taxable !== 0;
  const rawAmount = line?.amount;
  const haveAmount = rawAmount != null && String(rawAmount).trim() !== ''
    && Number.isFinite(Number(String(rawAmount).replace(/[$,]/g, '')));
  const amount = haveAmount ? Number(String(rawAmount).replace(/[$,]/g, '')) : null;
  if (rate == null || !haveTaxable || !haveAmount) return { ok: null, expected: null, delta: null, tol: null };
  if (rate === 0 && amount === 0) return { ok: null, expected: null, delta: null, tol: null };
  const expected = (rate / divisor) * taxable;
  const tol = lineTolerance(line?.tax_rate, taxable, divisor);
  const delta = expected - amount;
  return { ok: Math.abs(delta) <= tol, expected, delta, tol };
}

// ═══ WHICH UNIT ARE A STORED BILL'S LINE RATES IN? — ASK THE LINES ═══
//
// ⛔ NOTHING RECORDS THE UNIT, AND THE LINES THEMSELVES ARE THE ONLY HONEST WITNESS. A Florida bill's
// jurisdiction_rates hold MILLAGE while its total_tax_rate holds the converted PERCENT; no column says so.
// But every line carries taxable_value, tax_rate AND amount, so the unit is whichever divisor reproduces the
// amount:  3,830,385 × 4.4999 / 1000 = 17,236.36, the stored figure exactly. Ten lines agreeing is evidence;
// a single parcel-level ratio landing inside a window is a guess.
//
// ⛔ WHY NOT detectRateUnit, WHICH ALREADY ANSWERS THIS QUESTION. It infers from
// Σ(rates) ÷ (ad valorem ÷ taxable × 100) with a ±5% window, so (a) it needs two columns off the BILL ROW
// that a caller may not have selected, and (b) it misclassifies a MIXED-BASE bill — a school base ≈ 2× the
// non-school one drags the ratio to ~6.7 and it reports percent for a millage bill (docs/known-debt.md).
// That is worst on exactly the parcels this exists for. Per-line reconciliation has neither problem: each
// line is checked against ITS OWN base, so two bases verify independently.
//
// ⛔ MILLAGE ONLY ON POSITIVE EVIDENCE, AND THE ASYMMETRY IS THE POINT. Reading millage as percent makes a
// figure 10x too LARGE; the existing behaviour (assume percent, let the aggregate band refuse) makes it
// BLANK. A blank is recoverable in front of a client and a 10x tax is not, so this claims millage only when
// /1000 reconciles and /100 does not. Anything else — both fitting, neither fitting, too few lines carrying
// all three numbers — returns confident:false, and callers keep assuming percent.
//
// Returns `trials` and `best` as well, because the monitor wants the BETTER-fitting divisor to report failing
// lines against even when neither is clean, while a report wants to know whether the answer can be trusted.
// One resolver, two questions, no second implementation — this logic lived inline in check-tax-bills.mjs.
export function resolveRateUnit(lines) {
  const list = Array.isArray(lines) ? lines : [];
  const trials = [100, 1000].map((divisor) => {
    const res = list.map((l) => lineReconcile(l, divisor));
    return {
      divisor,
      res,
      checked: res.filter((r) => r.ok !== null).length,
      ok: res.filter((r) => r.ok === true).length,
      bad: res.filter((r) => r.ok === false).length,
    };
  });
  const [pct, mills] = trials;
  // Fewest failures wins for REPORTING purposes, percent breaking the tie — the monitor's existing rule.
  const best = mills.bad < pct.bad ? mills : pct;
  const out = (divisor, unit, confident, reason) => ({ divisor, unit, confident, reason, trials, best });

  if (!pct.checked && !mills.checked) {
    return out(100, 'percent', false, 'no line carries a rate, a taxable value and an amount together');
  }
  if (mills.bad === 0 && mills.ok > 0 && pct.bad > 0) return out(1000, 'mills', true, `${mills.ok} line(s) reconcile at /1000 and ${pct.bad} fail at /100`);
  if (pct.bad === 0 && pct.ok > 0 && mills.bad > 0) return out(100, 'percent', true, `${pct.ok} line(s) reconcile at /100 and ${mills.bad} fail at /1000`);
  // Both fitting is near-impossible (the expectations differ 10x and the tolerance is derived from the
  // printed rate's precision) but is NOT treated as an answer. Neither fitting means the transcription is
  // wrong, which is a different problem and not this function's to guess at.
  return out(100, 'percent', false,
    pct.bad === 0 && mills.bad === 0 ? 'both /100 and /1000 reconcile — cannot distinguish'
      : `neither divisor reconciles (percent ${pct.bad} bad, mills ${mills.bad} bad of ${Math.max(pct.checked, mills.checked)})`);
}

// ═══ RATE UNIT — millage or percent, for a STORED bill ═══
// ⛔ SUPERSEDED FOR NEW CALLERS by resolveRateUnit above, which asks the lines instead of inferring from a
// parcel-level ratio. Kept as the explicit fallback and for the scanner/breakdown, which pass the bill-row
// figures it needs; retiring it is logged separately rather than done here (it would mean touching the
// scanner in a commit about reports).
//
// ⛔ NOTHING RECORDS THE UNIT. tax_bills has no rate_unit column: a FL bill's jurisdiction_rates hold
// MILLAGE while its total_tax_rate holds the converted PERCENT, and the only way to tell is arithmetic.
// Σ line rates vs (ad valorem ÷ taxable × 100): ratio ≈ 10 → mills, else percent.
//
// ⛔ MOVED HERE FROM JurisdictionBreakdown (a React component), so a node script can use it without
// pulling in React — the backfill was otherwise about to sum a millage column as if it were percent and
// rely on a plausibility guard to catch the 10x error. Rate logic belongs beside the classifier.
//
// It also summed with a loose parseFloat, which reads '833-275-7297' as 833 — an LA County direct
// assessment prints a contact phone number where a rate belongs, and one of those would have swamped the
// ratio and forced 'percent' on a millage bill. It now sums through the classifier, like everything else.
export function detectRateUnit(rates, adValorem, taxable) {
  const sum = sumRatedRate(rates);
  const n = (v) => { const x = parseFloat(String(v ?? '').replace(/[$,%\s]/g, '')); return isNaN(x) ? null : x; };
  const adv = n(adValorem), tax = n(taxable);
  if (!sum || !adv || !tax) return 'percent';
  const derived = (adv / tax) * 100;
  const ratio = derived ? sum / derived : 0;
  return (ratio >= 9.5 && ratio <= 10.5) ? 'mills' : 'percent';
}
